import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import filtersConfig from "../Filters";
import { MultiSelect } from "react-multi-select-component";
import {
  Button,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import {
  getAllCustomers,
  getAllFilters,
  getInterfaceById,
} from "../../../services/configurationServices";
import { ClearFilter, FilterIcon } from "../../../assets/images";
import AlertTemplateMessage from "../../Configuration/AlertTemplate/AlertTemplateMessage";
import { MaterialReactTable } from "material-react-table";
import Swal from "sweetalert2";
import { getRecordingConfigurationDetails } from "../../../services/onVifService";
import { set } from "react-hook-form";

const RecordingConfig = ({ showSuccessToast }) => {
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const wrapperRef = useRef(null);
  const [filterValues, setFilterValues] = useState({});
  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recordingTableData, setRecordingTableData] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [interfaceData, setInterfaceData] = useState(null);
  const [displayMessage, setDisplayMessage] = useState("");

  // Enhanced configuration with extensible field mappings
  const getPageConfiguration = () => {
    const pageConfig = filtersConfig.find(
      (config) => config.pagename === "RecordingConfiguration",
    );
    if (!pageConfig) {
      console.warn("RecordingConfiguration not found in filters config");
      return { filters: {}, fieldMapping: {}, displayConfig: {} };
    }
    return pageConfig;
  };

  const pageConfig = getPageConfiguration();
  const pageFilters = pageConfig.filters;

  // Field mapping for API parameter values (ID fields)
  const apiFieldMapping = {
    Customer: "ClientId", // API parameter field for Customer
    State: "statemasterid",
    Devices: "Devices",
    Site: "SiteID", // API parameter field for Site
    Zone: "ZoneID",
    Interface: "InterfaceId",
    Device: "DeviceID",
  };

  // Use configured field mapping or fall back to default
  const fieldMapping = pageConfig.fieldMapping;

  // Get the actual field name from configuration (with fallback to default mapping)
  const getFieldName = (filterKey) => {
    const filterConfig = pageFilters[filterKey];
    if (filterConfig && filterConfig.fieldName) {
      return filterConfig.fieldName;
    }
    return fieldMapping[filterKey] || filterKey;
  };

  // Get placeholder text from configuration
  const getPlaceholderText = (filterKey, filterConfig) => {
    if (filterConfig.placeholder) {
      return filterConfig.placeholder;
    }
    return `Select ${filterConfig.label}${filterConfig.mandatory ? "*" : ""}`;
  };

  // Excel-like Filter Utility - filters options based on other active selections
  const getFilteredOptions = (filterKey, optionKey, labelKey) => {
    if (!filterData || !filterData.Data) {
      return [];
    }

    // Build a filter object with only selected values for each filter (excluding current filter)
    const activeSelections = {};
    Object.keys(pageFilters).forEach((key) => {
      const selected = filterValues[key];
      if (key !== filterKey && Array.isArray(selected) && selected.length > 0) {
        // For State filter, we need to use the statemasterid for comparison
        if (key === "State") {
          activeSelections[key] = selected.map((item) =>
            typeof item === "object" ? item.value : item,
          );
        } else {
          activeSelections[key] = selected.map((item) =>
            typeof item === "object" ? item.value : item,
          );
        }
      }
    });

    // Filter data based on all other active selections
    const filtered = filterData.Data.filter((item) => {
      return Object.entries(activeSelections).every(([key, selectedVals]) => {
        // Use the correct field for comparison
        const compareField = apiFieldMapping[key] || getFieldName(key);
        return selectedVals.includes(item[compareField]);
      });
    });

    // Get unique options for this filter
    const seen = new Set();
    return filtered
      .filter((item) => {
        const val = item[optionKey];
        if (!val || seen.has(val)) return false;
        seen.add(val);
        return true;
      })
      .map((item) => ({
        label: item[labelKey],
        value: item[optionKey],
      }))
      .sort((a, b) => {
        // Sort options in ascending order by label
        return a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
  };

  // Create options from API filterData with Excel-like filtering - now fully dynamic
  const getFilterOptions = (filterKey) => {
    if (!filterData || !filterData.Data) {
      return [];
    }

    const displayFieldName = getFieldName(filterKey); // Field for label (e.g., ClientName)
    const valueFieldName = apiFieldMapping[filterKey] || displayFieldName; // Field for value (e.g., ClientId)

    return getFilteredOptions(filterKey, valueFieldName, displayFieldName);
  };

  const handleFilterChange = (filterKey, value) => {

    if (filterKey == "Customer" && value.length > 0) {
      setSelectedCustomer(value[0]);
    }
    setFilterValues((prev) => {
      const newValues = {
        ...prev,
        [filterKey]: value,
      };

      // Smart dependency clearing - clear dependent filters when parent changes
      const filterConfig = pageFilters[filterKey];
      if (filterConfig) {
        // Find and clear all dependent filters
        Object.entries(pageFilters).forEach(([key, config]) => {
          if (config.parentFilter === filterKey) {
            newValues[key] = [];
            // Recursively clear dependencies
            const clearDependencies = (parentKey) => {
              Object.entries(pageFilters).forEach(([depKey, depConfig]) => {
                if (depConfig.parentFilter === parentKey) {
                  newValues[depKey] = [];
                  clearDependencies(depKey);
                }
              });
            };
            clearDependencies(key);
          }
        });
      }
      return newValues;
    });

    // Only reset table state when mandatory filters are changed
    const filterConfig = pageFilters[filterKey];
    if (filterConfig && filterConfig.mandatory) {
      // Reset table state when mandatory filters change
      setRecordingTableData([]);
      setFiltersApplied(false);
      setRowSelection({});
      setPagination({
        pageIndex: 0,
        pageSize: 10,
      });
    }
  };

  // Enhanced validation with better error messaging
  const isMandatoryFiltersMissing = () => {
    return Object.entries(pageFilters).some(([filterKey, filterConfig]) => {
      return (
        filterConfig.mandatory &&
        (!filterValues[filterKey] || filterValues[filterKey].length === 0)
      );
    });
  };

  // Get missing mandatory filters for better user feedback
  const getMissingMandatoryFilters = () => {
    return Object.entries(pageFilters)
      .filter(([filterKey, filterConfig]) => {
        return (
          filterConfig.mandatory &&
          (!filterValues[filterKey] || filterValues[filterKey].length === 0)
        );
      })
      .map(([filterKey, filterConfig]) => filterConfig.label);
  };

  // Enhanced filter button tooltip
  const getFilterButtonTooltip = () => {
    const missingFilters = getMissingMandatoryFilters();
    if (missingFilters.length > 0) {
      return `Please select: ${missingFilters.join(", ")}`;
    }
    return "Apply Filter";
  };

  // Handle global filter change with pagination reset
  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
    // Reset to first page whenever search changes
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await getAllFilters({ Entity: "Device" });
        setFilterData(response);
      } catch (error) {
        console.error("Error loading device filters:", error);
        setFilterData(null);
      }
    };

    fetchFilters();
  }, []);

  // Get filtered count for pagination
  const filteredCount = useMemo(() => {
    if (!globalFilter) return recordingTableData.length;

    return recordingTableData.filter((item) =>
      Object.values(item).some((value) =>
        String(value)
          .toLowerCase()
          .includes(String(globalFilter).toLowerCase()),
      ),
    ).length;
  }, [recordingTableData, globalFilter]);

// Utility to convert various date string formats to local timezone string
const formatUTCToOffset = (dateStr, offsetMinutes = 0) => {
  if (!dateStr) return "";

  let date;

  // dd-mm-yyyy hh:mm:ss (UTC)
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [day, month, yearAndTime] = dateStr.split("-");
    const [year, time] = yearAndTime.split(" ");
    const [hour, minute, second] = time.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // ISO format
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
    date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  }

  // yyyy-mm-dd hh:mm:ss (UTC)
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split(" ");
    const [year, month, day] = datePart.split("-");
    const [hour, minute, second] = timePart.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // fallback
  else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr;

  const offsetDate = new Date(date.getTime() + offsetMinutes * 60 * 1000);

  const pad = n => n.toString().padStart(2, "0");

  return `${pad(offsetDate.getUTCDate())}-${pad(offsetDate.getUTCMonth() + 1)}-${offsetDate.getUTCFullYear()} ` +
         `${pad(offsetDate.getUTCHours())}:${pad(offsetDate.getUTCMinutes())}:${pad(offsetDate.getUTCSeconds())}`;
};

  const getAdditionalInfo = () => {
    const interfaceId = filterValues.Interface?.[0]?.value;
    if (interfaceId) {
      setLoading(true);
      getInterfaceById(interfaceId).then((response) => {
        if (response && response.Data) {
          const interfaceData = response.Data;
          setInterfaceData(interfaceData);
          const { InterfaceIP, onvifusername, onvifuserpassword, OffsetMinutes,isonvif } = response?.Data[0];
          if(isonvif !== true){
            setDisplayMessage("This device is not Onvif supported.");
            setRecordingTableData([]);
            setLoading(false);
            setFiltersApplied(true);
            return
          }
          getRecordingConfigurationDetails(InterfaceIP, onvifusername, onvifuserpassword).then((recordingConfigResponse) => {
            if (recordingConfigResponse && recordingConfigResponse.Data.length > 0) {
              const recordingData = recordingConfigResponse.Data;
              // Format dates using offset
              const formattedRecordingData = recordingData.map((recording) => ({
                ...recording,
                EarliestTrack: formatUTCToOffset(recording.EarliestTrack, OffsetMinutes),
                LatestTrack: formatUTCToOffset(recording.LatestTrack, OffsetMinutes),
                Tracks: recording.Tracks.map((track) => ({
                  ...track,
                  From: formatUTCToOffset(track.From, OffsetMinutes),
                  To: formatUTCToOffset(track.To, OffsetMinutes),
                })),
              }));
              setRecordingTableData(formattedRecordingData);
              setLoading(false);
              //setSiteData(recordingData); // Assuming recordingData is in the correct format for siteData
              setFiltersApplied(true);
            } else {
              setDisplayMessage("No data found for the selected filters.");
              console.log("No recording configuration data found");
              setRecordingTableData([]);
              setLoading(false);
              setFiltersApplied(true);
            }
          }).catch((error) => {
            console.error("Error fetching recording configuration details:", error);
            setRecordingTableData([]);
            setLoading(false);
            setFiltersApplied(true);
          });
        }
      });
    }
    setFiltersApplied(true);
  };

  const recordings = [
    {
      RecordingToken: "OnvifRecordingToken_1",
      SourceId: "SourceId_1",
      Status: "Active",
      EarliestTrack: "2026-03-05T09:34:23Z",
      LatestTrack: "2026-03-06T06:00:01Z",
      Name: "IpCamera_1",
      Location: "Location",
      Description: "Description of source",
      Address: "http://www.onvif.org/ver10/schema/Profile",
      Content: "RecordContent",
      MaxRetentionTime: "PT0S",

      Tracks: [
        {
          VideoTrackToken: "videotracktoken_1",
          Type: "Video",
          From: "2026-03-05T09:34:23Z",
          To: "2026-03-06T06:00:01Z",
          Description: "VideoTrack",
        },
        {
          VideoTrackToken: "audiotracktoken_1",
          Type: "Audio",
          From: "2026-03-05T09:34:23Z",
          To: "2026-03-06T06:00:01Z",
          Description: "AudioTrack",
        },
        {
          VideoTrackToken: "metadatatracktoken_1",
          Type: "Metadata",
          From: "2026-03-05T09:34:23Z",
          To: "2026-03-06T06:00:01Z",
          Description: "MetadataTrack",
        },
      ],
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "RecordingToken",
        header: "Token",
        size: 250,
      },
      {
        accessorKey: "SourceId",
        header: "Source Id",
        size: 200,
      },
      {
        accessorKey: "Status",
        header: "Status",
        size: 150,
      },
      {
        accessorKey: "EarliestTrack",
        header: "Earliest",
      },
      {
        accessorKey: "LatestTrack",
        header: "Latest",
      },
      {
        accessorKey: "Name",
        header: "Name",
        size: 270,
      },
      // {
      //   accessorKey: "Location",
      //   header: "Location",
      // },
      {
        accessorKey: "Description",
        header: "Description",
        size: 330,
      },
      // {
      //   accessorKey: "Address",
      //   header: "Address",
      //   size: 300,
      // },
      // {
      //   accessorKey: "Content",
      //   header: "Content",
      //   size: 300,
      // },
      // {
      //   accessorKey: "MaxRetentionTime",
      //   header: "Max Retention Time",
      //   size: 300,
      // },
    ],
    [],
  );

  const trackColumns = [
    {
      accessorKey: "VideoTrackToken",
      header: "Token",
    },
    {
      accessorKey: "Type",
      header: "Type",
    },
    {
      accessorKey: "From",
      header: "From",
    },
    {
      accessorKey: "To",
      header: "To",
    },
    {
      accessorKey: "Description",
      header: "Description",
    },
  ];

  
  return (
    <div className="p-3">
      <div className="SiteMainContainer SiteTableMainDiv">
        <div className="DashboardFilterMainDiv">
          <div className="RefreshText" style={{ display: "flex" }}>
            {Object.entries(pageFilters)
              .sort(([, a], [, b]) => a.sequence - b.sequence)
              .map(([filterKey, filterConfig]) => (
                <div key={filterKey} className="inputTypeLabelContainer">
                  <label className="formFieldLabels">
                    {filterConfig.label}
                    {filterConfig.mandatory && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <div ref={wrapperRef}>
                    <MultiSelect
                      key={filterValues[filterKey]?.[0]?.value}
                      options={getFilterOptions(filterKey)}
                      value={filterValues[filterKey] || []}
                      onChange={(value) =>
                        handleFilterChange(
                          filterKey,
                          value.length > 1 ? [value[value.length - 1]] : value,
                        )
                      }
                      labelledBy={`Select ${filterConfig.label}`}
                      hasSelectAll={false}
                      isCreatable={false}
                      disableSearch={false}
                      className="SiteConfigSingleSelectDropdownContainer"
                      overrideStrings={{
                        selectSomeItems:
                          filterValues[filterKey] &&
                          filterValues[filterKey][0] &&
                          filterValues[filterKey][0].label
                            ? filterValues[filterKey][0].label
                            : getPlaceholderText(filterKey, filterConfig),
                        allItemsAreSelected:
                          filterValues[filterKey] &&
                          filterValues[filterKey][0] &&
                          filterValues[filterKey][0].label
                            ? filterValues[filterKey][0].label
                            : getPlaceholderText(filterKey, filterConfig),
                      }}
                    />
                  </div>
                </div>
              ))}
            <div className="HMFilterDiv">
              <Tooltip title={getFilterButtonTooltip()} placement="bottom">
                <Button
                  variant="outlined"
                  color="primary"
                  name="applyFilterBtn"
                  onClick={getAdditionalInfo}
                  disabled={isMandatoryFiltersMissing() || loading}
                  style={{
                    opacity: isMandatoryFiltersMissing() || loading ? 0.5 : 1,
                    cursor:
                      isMandatoryFiltersMissing() || loading
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  Get Recording Information
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="SiteMainHeightDiv">
          <div
            className="col-lg-12 m-0 TabulerReportMainDiv MatReactTableDiv"
            style={{ height: "calc(100vh - 230px)" }}
          >
            <div className="card-body p-0">
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="400px"
                  flexDirection="column"
                >
                  <CircularProgress size={60} thickness={4} />
                  <Box mt={2}>
                    <span style={{ fontSize: "16px", color: "#666" }}>
                      Fetching recording information via Onvif...
                    </span>
                  </Box>
                </Box>
              ) : recordingTableData.length === 0 ? (
                <div
                  style={{
                    width: "100%",
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <AlertTemplateMessage
                    message={
                      !filtersApplied || isMandatoryFiltersMissing()
                        ? "Please apply filters to see the recording configuration data."
                        : displayMessage
                    }
                  />
                </div>
              ) : (
                // <MaterialReactTable
                //     columns={columns}
                //     data={recordings}
                //     muiTableContainerProps={{
                //     className: "MaterialTableJs myTableContainer",
                //     sx: {
                //       maxHeight: "100%",
                //       overflow: "auto",
                //     },
                //   }}
                //   muiTableBodyRowProps={({ row }) => ({
                //     sx: {
                //         backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                //     },
                //     })}

                //     renderDetailPanel={({ row }) => (
                //         <Box sx={{ padding: 2 }}>

                //             <Typography variant="h6">Tracks</Typography>
                //             <MaterialReactTable
                //                 columns={trackColumns}
                //                 data={row.original.Tracks}   // ✅ fixed here
                //                 enableTopToolbar={false}

                //                 muiTableHeadCellProps={{
                //                     sx: {
                //                     backgroundColor: "#d3d3d3",
                //                     fontWeight: "bold",
                //                     },
                //                 }}

                //                 muiTableContainerProps={{
                //                     sx: {
                //                     border: "1px solid #dcdcdc",
                //                     borderRadius: "6px",
                //                     overflow: "auto",
                //                     marginBottom: "20px",
                //                     maxHeight: "250px",
                //                     },
                //                 }}

                //                 muiTableBodyRowProps={({ row }) => ({
                //                     sx: {
                //                     backgroundColor: row.index % 2 === 0 ? "#ffffff" : "",
                //                     },
                //                 })}
                //                 />
                //         </Box>
                //     )}
                // />
                <MaterialReactTable
                  columns={columns}
                  data={recordingTableData}
                  enableGlobalFilter
                  enableColumnFilters={false}
                  enableStickyHeader
                  enableColumnResizing
                  enableColumnOrdering
                  enableHiding
                  enableExpanding
                  columnResizeMode="onEnd"
                  globalFilterFn="contains"
                  getRowId={(row) => row.RecordingToken}
                  manualPagination
                  manualFiltering
                  rowCount={filteredCount}
                  state={{
                    pagination,
                    rowSelection,
                    globalFilter,
                  }}
                  onPaginationChange={setPagination}
                  onGlobalFilterChange={handleGlobalFilterChange}
                  muiTableContainerProps={{
                    className: "MaterialTableJs myTableContainer",
                    sx: {
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "auto",
                      overflowY: "auto",
                    },
                  }}
                  muiTableHeadProps={{
                    className: "myHeader",
                    sx: { display: "table-header-group" },
                  }}
                  renderTopToolbarCustomActions={() => (
                    <div className="d-flex justify-content-center align-items-center">
                      <h4 style={{ margin: 0 }}>
                        List of recordings{" "}
                        {recordingTableData.length > 0 && `(${recordingTableData.length} records)`}
                      </h4>
                    </div>
                  )}
                  displayColumnDefOptions={{
                    "mrt-row-expand": {
                      size: 40,
                      maxSize: 40,
                      minSize: 40,
                      muiTableHeadCellProps: {
                        sx: { width: "40px", padding: "0px" },
                      },
                      muiTableBodyCellProps: {
                        sx: { width: "40px", padding: "0px" },
                      },
                    },
                  }}
                  renderDetailPanel={({ row }) => (
                    <Box sx={{ padding: 1, width: "100%" }}>
                      <Typography variant="h6">Tracks</Typography>

                      <MaterialReactTable
                        columns={trackColumns}
                        data={row.original.Tracks}
                        enableTopToolbar={false}
                        enablePagination={false}
                        enableBottomToolbar={false}
                        muiTableContainerProps={{
                          className: "TabulerReportMainDiv",
                          sx: {
                            width: "100%",
                            maxWidth: "100%",
                          },
                        }}
                        muiTableProps={{
                          sx: {
                            width: "100%",
                            tableLayout: "fixed",
                          },
                        }}
                        muiTableHeadCellProps={{
                          sx: {
                            backgroundColor: "#7a7b7d",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordingConfig;
